import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col, Spinner } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";



// Redux actions
import {
    getNotifications,
    loadMoreNotifications,
    toggleNotificationReadUnread,
    deleteNotification
} from "../../../store/notifications/actions";
import { getUserAuthorized } from "../../../helpers/backend_helper";

// Custom components
import IconButton from "../../Common/IconButton";
import AccessControlWrapper from "../../Common/PermissionButton";
import ConfirmButton from "../../Common/ConfirmButton";
import * as url from "../../../helpers/url_helper";

const NotificationDropdown = (props) => {
    const { history } = props;
    // Declare a new state variable, which we'll call "menu"
    const [menu, setMenu] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const dispatch = useDispatch();

    // Get notifications from Redux store
    const {
        notifications,
        newNotifications,
        totalNotificationCount,
        newNotificationCount,
        unreadNotificationCount,
        isLoading,
        isLoadingMore,
        hasMoreNotifications,
        currentStartIndex
    } = useSelector(state => state.notifications);

    // Load notifications on component mount
    useEffect(() => {
        dispatch(getNotifications({
            headers: {
                "start-index": 0,
                "record-count": 10
            }
        }));
    }, [dispatch]);

    // Handle loading more notifications
    const handleLoadMore = () => {
        if (!isLoadingMore && hasMoreNotifications) {
            dispatch(loadMoreNotifications({
                headers: {
                    "start-index": currentStartIndex,
                    "record-count": 10
                }
            }));
        }
    };

    // Permission check function
    const onAccessCheck = async (contextUri, actionType) => {
        const result = await getUserAuthorized({
            headers: {
                "input-parameters": {
                    requestURI: contextUri,
                    requestMethod: actionType,
                },
            },
        });
        return result;
    };

    // Handle notification click (mark as read)
    const handleNotificationClick = async (notification) => {
        if (notification.hasBeenRead === "false") {
            try {
                const hasAccess = await onAccessCheck(url.TOGGLE_NOTIFICATION_READ_UNREAD, "post");
                const { code, desc, type } = hasAccess;

                if (
                    type === "USER_AUTHORIZATION_STATUS" &&
                    desc === "true" &&
                    code === "200"
                ) {
                    dispatch(toggleNotificationReadUnread([notification.notificationID.toString()]));
                } else {
                    alert("You do not have permission to perform this action.");
                }
            } catch (error) {
                console.error("Access check failed:", error);
                alert("You do not have permission to perform this action.");
            }
        }
    };

    // Handle mark as unread
    const handleMarkUnread = (notificationID, e) => {
        e.stopPropagation();
        dispatch(toggleNotificationReadUnread([notificationID.toString()]));
    };

    // Handle delete notification
    const handleDeleteNotification = (notificationID) => {
        dispatch(deleteNotification([notificationID.toString()]));
    };

    // Format notification time
    const formatTime = (dateTime) => {
        // Parse the backend datetime as UTC and convert to local time
        return moment.utc(dateTime, 'YYYY-MM-DD HH:mm:ss').local().fromNow();
    };

    // Check if notification is new
    const isNewNotification = (notificationID) => {
        return newNotifications && newNotifications.some(n => n.notificationID === notificationID);
    };


    return (
        <React.Fragment>
            <Dropdown
                isOpen={menu}
                toggle={() => {
                    if (!isModalOpen) {
                        setMenu(!menu);
                    }
                }}
                className="dropdown d-inline-block"
                tag="li"
            >
                <DropdownToggle
                    className="btn header-item noti-icon position-relative"
                    tag="button"
                    id="page-header-notifications-dropdown"
                >
                    <i className="bx bx-bell bx-tada" />
                    {unreadNotificationCount > 0 && (
                        <span className="badge bg-danger rounded-pill">
                            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                        </span>
                    )}
                </DropdownToggle>

                <DropdownMenu className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0" style={{ minWidth: "380px" }}>
                    <div className="p-3">
                        <Row className="align-items-center">
                            <Col>
                                <h6 className="m-0">
                                    Notifications
                                    {totalNotificationCount > 0 && (
                                        <span className="badge bg-primary ms-2">{totalNotificationCount}</span>
                                    )}
                                </h6>
                            </Col>
                        </Row>
                    </div>

                    <SimpleBar style={{ height: "300px", overflowX: "visible" }}>
                        {isLoading && notifications.length === 0 ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                                <Spinner color="primary" />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center p-4">
                                <i className="bx bx-bell-off font-size-20 text-muted mb-2"></i>
                                <p className="text-muted mb-0">{"No notifications found"}</p>
                            </div>
                        ) : (
                            <>
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.notificationID}
                                        className={`notification-item text-reset d-block position-relative ${notification.hasBeenRead === "false" ? "bg-light" : ""
                                            }`}
                                        style={{
                                            cursor: "pointer",
                                            transition: "all 0.2s ease",
                                            minHeight: "75px"
                                        }}
                                        onClick={() => handleNotificationClick(notification)}
                                        onMouseEnter={(e) => {
                                            const actionButtons = e.currentTarget.querySelector('.notification-actions');
                                            if (actionButtons) {
                                                actionButtons.style.height = '32px';
                                                actionButtons.style.opacity = '1';
                                                actionButtons.style.transform = 'translateY(0px)';
                                                actionButtons.style.visibility = 'visible';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            const actionButtons = e.currentTarget.querySelector('.notification-actions');
                                            if (actionButtons) {
                                                actionButtons.style.height = '0px';
                                                actionButtons.style.opacity = '0';
                                                actionButtons.style.transform = 'translateY(-10px)';
                                                setTimeout(() => {
                                                    if (actionButtons.style.height === '0px') {
                                                        actionButtons.style.visibility = 'hidden';
                                                    }
                                                }, 300);
                                            }
                                        }}
                                    >
                                        <div className="d-flex align-items-start px-3 py-2">
                                            <div className="me-2 flex-shrink-0" >
                                                <span className="avatar-title bg-primary rounded-circle" style={{ width: "28px", height: "28px", fontSize: "12px" }}>
                                                    <i className="bx bx-bell" />
                                                </span>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="align-items-center" style={{ display: "flex", justifyContent: "space-between" }}>
                                                    <h6 className="mt-0 mb-1 font-size-14 me-2">
                                                        {notification.title}
                                                        {isNewNotification(notification.notificationID) && (
                                                            <span className="badge bg-success ms-1 font-size-10">NEW</span>
                                                        )}
                                                    </h6>
                                                    {notification.hasBeenRead === "false" && (
                                                        <div className="bg-primary rounded-circle" style={{ width: "6px", height: "6px" }}></div>
                                                    )}
                                                </div>
                                                <div className="font-size-12 text-muted">
                                                    <p className="mb-1">{notification.message}</p>
                                                    <p className="mb-0">
                                                        <i className="mdi mdi-clock-outline me-1" /> {formatTime(notification.notifiedOn)}
                                                    </p>

                                                    {/* Action buttons that appear on hover */}
                                                    <div
                                                        className="notification-actions d-flex justify-content-end gap-1"
                                                        style={{
                                                            height: "0px",
                                                            opacity: "0",
                                                            overflow: "visible",
                                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                                            transform: "translateY(-10px)",
                                                            visibility: "hidden",
                                                            paddingTop: "4px",
                                                        }}
                                                    >
                                                        {notification.hasBeenRead === "true" && (
                                                            <AccessControlWrapper
                                                                actionType="post"
                                                                contextUri={url.TOGGLE_NOTIFICATION_READ_UNREAD}
                                                                history={history}
                                                            >
                                                                <IconButton
                                                                    icon="bx bx-envelope"
                                                                    color="warning"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleMarkUnread(notification.notificationID, e);
                                                                    }}
                                                                    title="Mark as unread"
                                                                />
                                                            </AccessControlWrapper>
                                                        )}

                                                        <div>
                                                            <ConfirmButton
                                                                onConfirm={() =>
                                                                    handleDeleteNotification(notification.notificationID)
                                                                }
                                                                message="Are you sure you want to delete this notification? This action cannot be undone."
                                                                renderButton={<IconButton.Delete size="sm" />}
                                                                contextUri={url.MANAGE_NOTIFICATION}
                                                                actionType="post"
                                                                history={history}
                                                                onModalOpen={() => setIsModalOpen(true)}
                                                                onModalClose={() => setIsModalOpen(false)}
                                                            />
                                                        </div>

                                                    </div>
                                                </div>
                                            </div>

                                        </div>



                                        <div className="border-bottom"></div>
                                    </div>
                                ))}

                                {isLoadingMore && (
                                    <div className="d-flex justify-content-center p-3">
                                        <Spinner size="sm" color="primary" />
                                    </div>
                                )}
                            </>
                        )}
                    </SimpleBar>

                    {notifications.length > 0 && hasMoreNotifications && (
                        <div className="p-2 border-top d-grid">
                            <button
                                className="btn btn-sm btn-link font-size-14 text-center"
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                            >
                                <i className="mdi mdi-arrow-right-circle me-1"></i>
                                <span>{isLoadingMore ? "Loading..." : "View More.."}</span>
                            </button>
                        </div>
                    )}
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default (NotificationDropdown);

NotificationDropdown.propTypes = {
    t: PropTypes.any
};